import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTheme, alpha } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import { closeUpgradeDialog } from 'src/redux/slices/planSlice';
import { isUserAdmin } from 'src/utils/roleUtils';

export default function UpgradePromptDialog() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const authSites = useSelector((state) => state.auth.sites);
  const isAdmin = isUserAdmin(authSites);

  const {
    upgradeDialogOpen,
    upgradeDialogRequiredPlan,
    upgradeDialogFeatureLabel,
    upgradeDialogFeatureType,
    upgradeDialogLimitValue,
    currentPlanTier,
  } = useSelector((state) => state.plan);

  const handleClose = () => dispatch(closeUpgradeDialog());
  const handleUpgrade = () => { handleClose(); navigate('/pricing'); };

  if (!upgradeDialogOpen) return null;

  const planName = upgradeDialogRequiredPlan
    ? upgradeDialogRequiredPlan.charAt(0).toUpperCase() + upgradeDialogRequiredPlan.slice(1).toLowerCase()
    : 'Standard';

  const currentPlanName = currentPlanTier
    ? currentPlanTier.charAt(0).toUpperCase() + currentPlanTier.slice(1).toLowerCase()
    : 'Free';

  const isNumeric = upgradeDialogFeatureType === 'NUMERIC';

  let descriptionContent = null;

  if (isNumeric) {
    if (isAdmin) {
      descriptionContent = (
        <>
          Your current <strong>{currentPlanName}</strong> plan includes a limit of only{' '}
          <strong>{upgradeDialogLimitValue}</strong> {upgradeDialogFeatureLabel || 'items'}. To increase this limit, please upgrade to the{' '}
          <strong>{planName}</strong> plan or higher.
        </>
      );
    } else {
      descriptionContent = (
        <>
          Your current <strong>{currentPlanName}</strong> plan includes a limit of only{' '}
          <strong>{upgradeDialogLimitValue}</strong> {upgradeDialogFeatureLabel || 'items'}. Please contact your administrator to upgrade your plan and increase this limit.
        </>
      );
    }
  } else if (isAdmin) {
    descriptionContent = (
      <>
        The feature <strong>{upgradeDialogFeatureLabel || 'this feature'}</strong> is available on the{' '}
        <strong>{planName}</strong> plan or higher. Upgrade your subscription to unlock.
      </>
    );
  } else {
    descriptionContent = (
      <>
        The feature <strong>{upgradeDialogFeatureLabel || 'this feature'}</strong> is available on the{' '}
        <strong>{planName}</strong> plan or higher. Please contact your administrator.
      </>
    );
  }

  return (
    <Dialog open={upgradeDialogOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            color: theme.palette.warning.main,
          }}
        >
          <Iconify icon="solar:lock-bold-duotone" width={24} height={24} />
        </Box>
        <Typography variant="h6">Upgrade Your Plan</Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {descriptionContent}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="outlined" color="inherit" onClick={handleClose}>
          {isAdmin ? 'Cancel' : 'Close'}
        </Button>
        {isAdmin && (
          <Button variant="contained" color="primary" onClick={handleUpgrade} autoFocus>
            Upgrade Now
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
